/**
 * Provides the `getFileData` utility function for fetching and processing data from a URL, and its associated `GetFileDataOptions` and `GetFileDataResult` types.
 * @module getFileData
 */
// Forward namespace for docs and import processing functions
// We're doing this so typedoc understands this as a namespace and not a module
import * as processCSVNamespace from './process-csv';
import * as processJSONNamespace from './process-json';
import * as processXMLNamespace from './process-xml';
/**
 * Fetches data from a given URL and processes it as CSV, JSON (array of objects), or XML, based on the specified type. Returns structured information about the processed data.
 *
 * This function uses overloads to provide specific return types based on the input `type`.
 *
 * @since 5.10 Initial implementation.
 * @param type - The type of the file ('csv', 'json', 'xml'). Determines how the data is processed.
 * @param url - The URL from which to fetch the data file.
 * @param options - Optional configuration object. Currently only used for 'csv' type to specify `headerRow`, `separator`, and `hasHeaders`.
 * @return A Promise that resolves with an object containing processed data specific to the type (ProcessCsvData, ProcessJsonData, or ProcessXmlData), or resolves with `null` if fetching or processing fails.
 *
 * @throws Will propagate errors from `fetch` if the network request itself fails fundamentally (e.g., DNS error, CORS issue if not handled server-side). Errors during processing (invalid JSON/XML, non-array JSON) are caught and result in `null`.
 *
 * @example Importing the getFileData utility
 * ```typescript
 * import { getFileData } from '@automator/shared/utils/get-file-data';
 * ```
 *
 * @example Fetch and process CSV
 * ```typescript
 * const csvData = await getFileData( 'csv', '/path/to/data.csv', { separator: ';' } );
 *
 * if ( csvData ) {
 *     console.log( __( 'CSV Headers:', csvData.headers ); // ProcessCsvData
 *     console.log( __( 'CSV Rows:', csvData.numberOfRows );
 * }
 * ```
 *
 * @example Fetch and process JSON
 * ```typescript
 * const jsonData = await getFileData( 'json', '/path/to/data.json' );
 *
 * if ( jsonData ) {
 *     console.log( __( 'JSON Keys:', jsonData.keys ); // Type is ProcessJsonData
 *     console.log( __( 'JSON Items:', jsonData.numberOfItems );
 * }
 * ```
 *
 * @example Fetch and process XML
 * ```typescript
 * const xmlData = await getFileData( 'xml', '/path/to/data.xml' );
 *
 * if ( xmlData ) {
 *     console.log( __( 'XML Root:', xmlData.rootElementName ); // Type is ProcessXmlData
 *     console.log( __( 'XML Children:', xmlData.numberOfChildElements );
 * }
 * ```
 *
 * @example Handling errors
 * ```typescript
 * const data = await getFileData( 'json', '/path/to/invalid-or-missing.json' );
 * if ( data === null ) {
 *     console.log( __( 'Failed to fetch or process JSON data.', 'uncanny-automator' ) );
 * }
 * ```
 */
export async function getFileData(type, url, options = {}) {
    try {
        const response = await fetch(url);
        // Check for network errors (e.g., 404, 500)
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}`);
        }
        const data = await response.text();
        switch (type) {
            case 'csv':
                return processCSVNamespace.processCSV(data, options);
            case 'json':
                return processJSONNamespace.processJSON(data);
            case 'xml':
                return processXMLNamespace.processXML(data);
            default:
                // This case should be unreachable due to the narrowed 'type' parameter, but it's good practice for exhaustiveness checking.
                // We can assert never or throw a more specific error.
                const exhaustiveCheck = type;
                throw new Error(`Unsupported data type: ${exhaustiveCheck}`);
        }
    }
    catch (error) {
        console.error(`Error fetching or processing ${type} from ${url}:`, error);
        return null;
    }
}
;
// Forward namespace for docs and import processing functions
// We're doing this so typedoc understands this as a namespace and not a module
export { processCSVNamespace as processCSV };
export { processJSONNamespace as processJSON };
export { processXMLNamespace as processXML };
